import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  CashSession,
  CashMovement,
  CashSessionFilters,
  OpenCashSessionInput,
  CloseCashSessionInput,
  CreateCashMovementInput,
} from '@erp/data-access/interfaces';

const SESSIONS_KEY = ['cash_sessions'] as const;
const MOVEMENTS_KEY = ['cash_movements'] as const;
const PAYMENTS_KEY = ['payments'] as const;

/**
 * Always fetches the full sessions list and filters in memory so caisse
 * tabs (open/closed, by register) hit the prewarmed cache offline.
 */
export function useCashSessions(filters?: CashSessionFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...SESSIONS_KEY, undefined],
    queryFn: () => ds.caisse.listSessions(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { bankAccountId, isClosed, dateFrom, dateTo } = filters;
    return query.data.filter((s) => {
      if (bankAccountId && s.bankAccountId !== bankAccountId) return false;
      if (isClosed !== undefined && s.isClosed !== isClosed) return false;
      if (dateFrom && s.openedAt < dateFrom) return false;
      if (dateTo && s.openedAt > dateTo) return false;
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useCashSession(id: string | undefined) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...SESSIONS_KEY, 'detail', id],
    queryFn: () => ds.caisse.getSessionById(id!),
    enabled: !!id,
    placeholderData: () => (id ? listCacheFallback<CashSession>(qc, SESSIONS_KEY, id) : undefined),
  });
}

export function useOpenCashSession(bankAccountId: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SESSIONS_KEY, 'open', bankAccountId],
    queryFn: () => ds.caisse.getOpenSession(bankAccountId!),
    enabled: !!bankAccountId,
  });
}

export function useOpenCashSessions() {
  const ds = useDataService();
  return useQuery({
    queryKey: [...SESSIONS_KEY, 'open-list'],
    queryFn: () => ds.caisse.listOpenSessions(),
  });
}

export function useCashMovements(sessionId: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...MOVEMENTS_KEY, sessionId],
    queryFn: () => ds.caisse.listMovements(sessionId!),
    enabled: !!sessionId,
  });
}

// ---- Offline-aware mutations ----
//
// On desktop with no connection, the three caisse writes (open / close /
// movement) go through the sync engine outbox so the cashier can keep
// ringing up sales. The engine drains the queue automatically when the
// network comes back. The optimistic shapes below are good enough for
// the UI to keep rendering until the real server row arrives.

const nowIso = () => new Date().toISOString();
const SESSION_PREFIX = 'OFFLINE-S-';
const MOVEMENT_PREFIX = 'OFFLINE-M-';

export function useOpenSession() {
  const ds = useDataService();
  const { user } = useAuth();
  return useResilientMutation<OpenCashSessionInput, CashSession>({
    entity: 'caisse_sessions',
    op: 'custom',
    method: 'openSession',
    invalidate: [SESSIONS_KEY, MOVEMENTS_KEY],
    online: (input) =>
      ds.caisse.openSession({ ...input, openedBy: input.openedBy ?? user?.id }),
    optimistic: (input, clientId) => ({
      id: clientId,
      number: SESSION_PREFIX + clientId.slice(0, 6).toUpperCase(),
      bankAccountId: input.bankAccountId,
      openedBy: input.openedBy ?? user?.id ?? '',
      openedAt: nowIso(),
      openingBalance: input.openingBalance,
      closedBy: null,
      closedAt: null,
      expectedBalance: null,
      countedBalance: null,
      difference: null,
      isClosed: false,
      notes: input.notes ?? null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }),
    // Insert the new open session into every cached sessions list AND the
    // "open-list" snapshot so the caisse dashboard shows it immediately,
    // even when offline. The standard `invalidate` only triggers on a
    // confirmed online write — on the offline path nothing would update
    // the lists until reconnect without this.
    applyOptimistic: (qc, newSession) => {
      qc.setQueriesData<CashSession[]>({ queryKey: SESSIONS_KEY }, (old) =>
        Array.isArray(old) ? [newSession, ...old] : old,
      );
      qc.setQueryData<CashSession[]>([...SESSIONS_KEY, 'open-list'], (old) =>
        Array.isArray(old) ? [newSession, ...old] : [newSession],
      );
      qc.setQueryData<CashSession>([...SESSIONS_KEY, 'detail', newSession.id], newSession);
    },
  });
}

export function useCloseSession() {
  const ds = useDataService();
  const { user } = useAuth();
  return useResilientMutation<CloseCashSessionInput, CashSession>({
    entity: 'caisse_sessions',
    op: 'custom',
    method: 'closeSession',
    invalidate: [SESSIONS_KEY, MOVEMENTS_KEY, PAYMENTS_KEY],
    online: (input) =>
      ds.caisse.closeSession({ ...input, closedBy: input.closedBy ?? user?.id }),
    // We don't have the full session locally — return a minimal shape
    // tagged as closed so the dialog can dismiss. The next cache
    // refetch on reconnect will replace it with the canonical row.
    optimistic: (input) => ({
      id: input.sessionId,
      number: SESSION_PREFIX + input.sessionId.slice(0, 6).toUpperCase(),
      bankAccountId: '',
      openedBy: '',
      openedAt: nowIso(),
      openingBalance: 0,
      closedBy: input.closedBy ?? user?.id ?? '',
      closedAt: nowIso(),
      expectedBalance: input.expectedBalance,
      countedBalance: input.countedBalance,
      difference: input.countedBalance - input.expectedBalance,
      isClosed: true,
      notes: input.notes ?? null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }),
    // Pull the now-closed session out of the open-list and stamp the
    // closed shape onto every list/detail entry that references it.
    applyOptimistic: (qc, closedSession) => {
      qc.setQueryData<CashSession[]>([...SESSIONS_KEY, 'open-list'], (prev) =>
        Array.isArray(prev) ? prev.filter((s) => s.id !== closedSession.id) : prev,
      );
      qc.setQueriesData<CashSession[]>({ queryKey: SESSIONS_KEY }, (old) =>
        Array.isArray(old)
          ? old.map((s) => (s.id === closedSession.id ? { ...s, ...closedSession } : s))
          : old,
      );
      qc.setQueryData<CashSession>(
        [...SESSIONS_KEY, 'detail', closedSession.id],
        (prev) => (prev ? { ...prev, ...closedSession } : closedSession),
      );
    },
  });
}

export function useCancelEmptySession() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.caisse.cancelEmptySession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SESSIONS_KEY });
      qc.invalidateQueries({ queryKey: MOVEMENTS_KEY });
    },
  });
}

export function useCreateCashMovement() {
  const ds = useDataService();
  const { user } = useAuth();
  return useResilientMutation<CreateCashMovementInput, CashMovement>({
    entity: 'caisse_movements',
    op: 'create',
    invalidate: [MOVEMENTS_KEY, SESSIONS_KEY],
    online: (input) =>
      ds.caisse.createMovement({ ...input, createdBy: input.createdBy ?? user?.id }),
    optimistic: (input, clientId) => ({
      id: clientId,
      number: MOVEMENT_PREFIX + clientId.slice(0, 6).toUpperCase(),
      sessionId: input.sessionId,
      movementType: input.movementType,
      category: input.category,
      amount: input.amount,
      description: input.description ?? null,
      paymentId: null,
      createdBy: input.createdBy ?? user?.id ?? null,
      createdAt: nowIso(),
    }),
    // Append to the session's movements list so the caisse view updates
    // even when offline (where the invalidate-then-refetch path can't run).
    applyOptimistic: (qc, newMovement) => {
      qc.setQueryData<CashMovement[]>([...MOVEMENTS_KEY, newMovement.sessionId], (old) =>
        Array.isArray(old) ? [...old, newMovement] : [newMovement],
      );
    },
  });
}
